import {
  FaXTwitter,
  FaSquareFacebook,
  FaGoogle,
  FaLinkedin,
  FaInstagram,
  FaYoutube
} from "react-icons/fa6";

import { IconButton } from "../button/icon-button";
import {
  Link,
  Stack,
  Wrap,
  WrapItem,
  type LinkProps,
  type StackProps,
  type WrapProps,
  type WrapItemProps
} from "../internal/exports";

export const FooterSocialMediaLinks = ({
  facebookUrl,
  twitterUrl,
  googleUrl,
  linkedinUrl,
  instagramUrl,
  youtubeUrl,
  className,
  order,
  id
}: {
  facebookUrl?: string;
  twitterUrl?: string;
  googleUrl?: string;
  linkedinUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
} & Pick<StackProps, "order" | "className" | "id">): JSX.Element => {
  return (
    <Stack
      gap={0.5}
      direction="row"
      alignItems="center"
      mr={10}
      className={className}
      order={order}
      id={id}
    >
      {[
        {
          link: facebookUrl,
          icon: <FaSquareFacebook size="1.1em" />,
          label: "facebook"
        },
        {
          link: twitterUrl,
          icon: <FaXTwitter size="1.1em" />,
          label: "twitter"
        },
        {
          link: googleUrl,
          icon: <FaGoogle size="1.1em" />,
          label: "google"
        },
        {
          link: linkedinUrl,
          icon: <FaLinkedin size="1.1em" />,
          label: "linkedin"
        },
        {
          link: instagramUrl,
          icon: <FaInstagram size="1.1em" />,
          label: "instagram"
        },
        {
          link: youtubeUrl,
          icon: <FaYoutube size="1.1em" />,
          label: "youtube"
        }
      ].map(({ link, icon, label }, index) => {
        if (!link) return null;

        return (
          <IconButton
            h="auto"
            minW="auto"
            bg="inherit"
            color="inherit"
            round={false}
            as={Link}
            key={index}
            icon={icon}
            href={link}
            className="ehq-participant-footer__social-links__link"
            variant="unstyled"
            aria-label={label}
            p="5px"
            css={{
              textDecoration: "none",
              "& svg": { color: "currentColor" },
              _hover: { color: "inherit", bg: "inherit" },
              _focusVisible: {
                outline: "-webkit-focus-ring-color auto 1px !important",
                outlineOffset: "1px !important",
                borderRadius: 0,
                boxShadow: "none"
              }
            }}
          />
        );
      })}
    </Stack>
  );
};

export const FooterLink = ({
  className,
  children,
  id
}: Pick<WrapItemProps, "children" | "className" | "id">): JSX.Element => {
  return (
    <WrapItem
      alignItems="center"
      cursor="pointer"
      id={id}
      className={className}
      css={{
        "& a:hover": {
          textDecoration: "underline !important"
        },
        "& a:focus-visible": {
          outline: "-webkit-focus-ring-color auto 1px !important",
          outlineOffset: "1px !important",
          borderRadius: 0,
          boxShadow: "none"
        }
      }}
    >
      {children}
    </WrapItem>
  );
};

export const FooterLinks = ({
  className,
  children,
  order,
  id
}: Pick<WrapProps, "children" | "order" | "className" | "id">): JSX.Element => {
  return (
    <Wrap
      mx={5}
      p={1}
      gap={4}
      align="center"
      fontWeight={400}
      whiteSpace="nowrap"
      width={{ base: "100%", lg: "calc(100vw - 350px)" }}
      flex={{ lg: 3 }}
      justify={{ base: "center", lg: "initial" }}
      className={className}
      order={order}
      id={id}
    >
      {children}
    </Wrap>
  );
};

export const FooterLogo = ({
  className,
  children,
  order,
  href,
  id
}: Pick<
  LinkProps,
  "children" | "order" | "className" | "id" | "href"
>): JSX.Element => {
  return (
    <Link
      maxW={150}
      target="_blank"
      fontWeight={600}
      textAlign="center"
      rel="noopener noreferrer"
      href={href || "https://granicus.com"}
      className={className}
      order={order}
      id={id}
      css={{
        _focusVisible: {
          outline: "-webkit-focus-ring-color auto 1px !important",
          outlineOffset: "1px !important",
          borderRadius: 0,
          boxShadow: "none"
        }
      }}
    >
      {children}
    </Link>
  );
};

export const Footer = ({
  className,
  boxShadow,
  fontSize,
  background,
  children,
  color,
  id,
  position
}: Pick<
  StackProps,
  | "children"
  | "color"
  | "background"
  | "fontSize"
  | "boxShadow"
  | "className"
  | "id"
  | "position"
>): JSX.Element => {
  return (
    <Stack
      px={5}
      py={4}
      wrap="wrap"
      align="center"
      justify="center"
      gap={{ base: 4, lg: "auto" }}
      direction={{ base: "column", lg: "row" }}
      justifyContent={{ base: "center", lg: "space-between" }}
      background={background || "black"}
      boxShadow={boxShadow || "sm"}
      fontSize={fontSize || "sm"}
      color={color || "white"}
      className={className}
      id={id}
      width="100%"
      position={position}
    >
      {children}
    </Stack>
  );
};

export default Footer;
